import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ErrorMessage from '../components/errorMessage';
import {
  createDocumentCheckCognito,
  pollDocumentCheckCognito,
} from '../utils/api';
// eslint-disable-next-line no-unused-vars
import { Onfido } from 'onfido-sdk-ui';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import './PassportVerificationContainer.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Row } from 'react-bootstrap';
import { currentBrand } from '../utils/brand';
import { getTranslation } from '../utils/translations';
import { onfidoBrandThemes } from '../utils/onfidoBrandThemes'
import useJWT from '../utils/useJWT';

const PassportVerificationStepEnum = {
  Verification: 'Verification',
  Retry: 'Retry',
  Loading: 'Loading',
  Verified: 'Verified',
  Registered: 'Registered',
  Errored: 'Errored',
  Polling: 'Polling',
};

export const PassportVerificationCognitoContainer = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [sdkToken, setSdkToken] = useState(null);
  const [applicantId, setApplicantId] = useState(null);
  const [onfidoOut] = useState(null);
  const [uiStep, _setUiStep] = useState(PassportVerificationStepEnum.Loading);
  const [jwt] = useJWT();

  const timeOutErrorMessage = getTranslation('timeOutErrorMessage');
  const countryMessageForCheckError = getTranslation('countryMessageForCheckError');
  const brandName = process.env.REACT_APP_BRAND;

  const setUiStep = (newStep) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'onfido_verification_step',
        'step': newStep
      });
    }

    _setUiStep(newStep);
  };

  useEffect(() => {
    if (!jwt) {
      return;
    }

    const fetchSdkToken = async () => {
      try {
        const response = await axios.post('/api/document-verification-cognito',
          null,
          {
            headers: {
              'Authorization': `Bearer ${jwt}`
            }
          });

        setSdkToken(response.data.sdkToken);
        setApplicantId(response.data.applicantId);
        setUiStep(PassportVerificationStepEnum.Verification);
      } catch (e) {
        console.log(`Unable to fetch SDK token: ${e.message}`);
        setErrorMessage(e.message);
        setUiStep(PassportVerificationStepEnum.Retry);
      }
    }

    fetchSdkToken();
  }, [jwt]);

  const passportVerification = () => {
    window.setTimeout(initializeVerificationSDK, 400);
    return <div id="onfido-mount" className="onfido-mount" />;
  };

  const initializeVerificationSDK = () => {
    if (onfidoOut && !document.querySelector('#onfido-mount')) {
      console.error('Unable to initialize Onfido SDK, cannot find #onfido-mount');
      return;
    }

    const theme = onfidoBrandThemes[brandName] || onfidoBrandThemes.frontnode

    Onfido.init({
      token: sdkToken,
      containerId: 'onfido-mount',
      onComplete,
      steps: [
        {
          type: 'welcome',
        },
        {
          type: 'document',
          options: {
            documentTypes: {
              driving_licence: false,
              national_identity_card: true,
              residence_permit: false,
              passport: true,
            },
          },
        },
        {
          type: 'face',
          options: {
            requestedVariant: 'video',
          },
        },
        {
          type: 'complete',
        },
      ],
      theme,
      onError,
    });
  };

  const onComplete = async () => {
    try {
      await createDocumentCheckCognito(applicantId, jwt);
    } catch (e) {
      console.error(`Error when creating document check: ${e.message}`);
      setUiStep(PassportVerificationStepEnum.Retry);
      return;
    }

    try {
      tearDown();
      setUiStep(PassportVerificationStepEnum.Polling);
      const isVerified = await pollDocumentCheckCognito(jwt);
      if (!isVerified) {
        onError(new Error(timeOutErrorMessage));
        return;
      }
      setUiStep(PassportVerificationStepEnum.Verified);
      await new Promise((resolve) => setTimeout(resolve, 4000));
      window.location.href = `${process.env.REACT_APP_DASHBOARD_LOGIN_URL}login?action=login&country=eu`;
    } catch (e) {
      console.error(`Error: ${e.message}`);
      if (e.message === 'Verification reports failed') {
        setErrorMessage(
          `There were some issues raised during your verification process. Please write our support at ${
            currentBrand().emailRegistration
          } and make sure to mention the applicant id: ${applicantId}`,
        );
        setUiStep(PassportVerificationStepEnum.Errored);
        return;
      }
      if (
        e.message &&
        e.message.startsWith(countryMessageForCheckError)
      ) {
        setErrorMessage(e.message);
      }
      setUiStep(PassportVerificationStepEnum.Retry);
      tearDown();
    }
  };

  const onError = (err) => {
    console.log('OnFido onError handler got:', err);
    setErrorMessage(err.message);
    setUiStep(PassportVerificationStepEnum.Retry);
    tearDown();
  };

  const loader = () => (
    <div className="spinner">
      <div className="dot1" />
      <div className="dot2" />
    </div>
  );

  const tearDown = () => {
    if (!onfidoOut) {
      return;
    }
    onfidoOut.tearDown();
  };

  const uiForStep = () => {
    if (uiStep === PassportVerificationStepEnum.Loading) {
      return loader();
    }
    if (uiStep === PassportVerificationStepEnum.Verification) {
      return passportVerification();
    }
    if (uiStep === PassportVerificationStepEnum.Verified) {
      return successView();
    }
    if (uiStep === PassportVerificationStepEnum.Registered) {
      return registeredView();
    }
    if (uiStep === PassportVerificationStepEnum.Retry) {
      return retryView();
    }
    if (uiStep === PassportVerificationStepEnum.Polling) {
      return polling();
    }
  };

  const polling = () => {
    document.getElementById('onfido-sdk').style.display = 'none';

    return (
      <div className="polling">
        <h2>{getTranslation('verificationInProgress')}</h2>
        <p>{getTranslation('verificationTime')}</p>
        <div className="spinner">
          <div className="dot1"></div>
          <div className="dot2"></div>
        </div>
      </div>
    );
  };

  const retryView = () => {
    document.getElementById('onfido-sdk').style.display = 'none';

    return (
      <div className="polling form">
        <h2>{getTranslation('tryAgain')}</h2>
        <div className="try-again">
          <Button
            variant="light"
            className="btn cancel"
            onClick={onCancelClick}
          >
            {getTranslation('cancel')}
          </Button>
          <Button className="btn primary" onClick={onRetryClick}>
            {getTranslation('retry')}
          </Button>
        </div>
      </div>
    );
  };

  const onRetryClick = () => {
    // eslint-disable-next-line no-restricted-globals
    location.reload();
  };

  const onCancelClick = () => {
    // eslint-disable-next-line no-restricted-globals
    location.href = process.env.REACT_APP_LANDING_PAGE_URL;
  };

  const successView = () => (
    <div className="polling">
      <h2>{getTranslation('successTitle')}</h2>
      <p>{getTranslation('successMessage')}</p>
      <p>
        {getTranslation('redirectLinkMessage')}{' '}
        <a href={`${process.env.REACT_APP_DASHBOARD_LOGIN_URL}login?action=login&country=eu`}>
          {getTranslation('dashboardLink')}
        </a>.
      </p>
    </div>
  );

  const registeredView = () => (
    <div>
      <h3>{getTranslation('readyToLoginTitle')}</h3>
      <p>{getTranslation('readyToLoginMessage')}</p>
      <p>
        {getTranslation('visit')}&nbsp;
        <a href={`${process.env.REACT_APP_DASHBOARD_LOGIN_URL}login?action=login&country=eu`}>
          {getTranslation('dashboardLink')}&nbsp;
        </a>
        {getTranslation('ableToLoginMessage')}
      </p>
    </div>
  );

  if (!jwt) {
    return <div>Not authenticated</div>;
  }

  return (
    <div className="content">
      <ErrorMessage
        error={
          uiStep === PassportVerificationStepEnum.Retry ||
          uiStep === PassportVerificationStepEnum.Errored
        }
        errorMessage={errorMessage}
      />
      {uiForStep()}
    </div>
  );
};
